import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFCheckbox } from 'src/components/hook-form';
import { ReservationGeneral } from 'src/models/ReservationGeneral';
import { usePatchRegistrationStepMutation } from 'src/redux/rtkQuery/apiSlice';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { brandConfig } from 'src/config';
import HelpLink from './HelpLinks';
import { RegistrationFooter } from './RegistrationFooter';
import { RegistrationPicklistItem } from './types';

const resolver = yupResolver(
  Yup.object().shape({
    resort_disclosures: Yup.boolean().oneOf([true], '').required(''),
  })
);

const RegistrationResortDisclosureForm = ({
  reservation,
  stepCompleted,
  callbackCancel,
  callbackContinue,
}: {
  reservation: ReservationGeneral;
  stepCompleted: boolean;
  callbackCancel: VoidFunction;
  callbackContinue: VoidFunction;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [sendPatchRegistrationStep] = usePatchRegistrationStepMutation();
  const { resort_disclosures } = reservation;
  const methods = useForm({
    resolver,
    defaultValues: { resort_disclosures: stepCompleted },
    mode: 'all',
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isLoading, isValid },
  } = methods;
  const onSubmit = handleSubmit(async (formData) => {
    if (stepCompleted) {
      // Step is already completed, no need to update
      // Exit and proceed to next step
      callbackContinue();
      return;
      // Updates detected, should post updates
    }
    const sfPayload = {
      reservationName: reservation.name,
      registrationStep: RegistrationPicklistItem.RESORT_DISCLOSURES,
    };
    await sendPatchRegistrationStep(sfPayload)
      .then((data: any) => {
        if (data.error) {
          console.error(data.error.message);
          return;
        }
        enqueueSnackbar('Resort disclosures completed.', { variant: 'success' });
        callbackContinue();
      })
      .catch((putError: any) => {
        console.error('error on update stay reason', putError);
      });
  });
  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Divider />
        {Object.keys(resort_disclosures).map((key) => {
          return (
            <div key={key + '-disclosure'}>
              <Typography variant="h6">{key}</Typography>
              <Typography variant="body1">{resort_disclosures[key]}</Typography>
            </div>
          );
        })}
        <Box>
          <RHFCheckbox
            disabled={isSubmitting}
            labelPlacement="end"
            name="resort_disclosures"
            label={
              <Typography variant="subtitle2" pl={0.5}>
                I acknowledge resort disclosures
              </Typography>
            }
            sx={{ ml: 0 }}
            onChange={onSubmit}
          />
        </Box>
        {brandConfig.helpLinks.guest_registration && (
          <HelpLink label="guest_registration" value={brandConfig.helpLinks.guest_registration} />
        )}
        <RegistrationFooter
          backButton={
            <Button disabled={isSubmitting} onClick={callbackCancel}>
              Back
            </Button>
          }
        >
          <LoadingButton
            type="submit"
            loading={isSubmitting}
            disabled={!isValid}
            variant="contained"
          >
            Continue
          </LoadingButton>
        </RegistrationFooter>
      </Stack>
    </FormProvider>
  );
};

export default RegistrationResortDisclosureForm;
